import { useCallback } from "react";

import { useSelector } from "@jti/store/reduxHooks";
import type { CheckoutGTMFlowType, TrackingEventsAdditionalData } from "@jti/tracker";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { buildCommerceBaseData, getTemplateName } from "@checkout-v2/shared/utils";

import { useCartItems } from "../useCartItems";
import { useCartPrices } from "../useCartPrices";

type UseGtmPaymentInfoReturn = {
  trackGtmPaymentInfo: (
    code: string,
    checkoutFlowType: CheckoutGTMFlowType | null,
    paymentInfoPrefilled: boolean
  ) => void;
};

export const useGtmPaymentInfo = (): UseGtmPaymentInfoReturn => {
  const sharedData = getTemplateName("checkout");
  const { data: cartItems } = useCartItems();
  const { data: cartPrices } = useCartPrices();
  const paymentSection = useSelector((state) => state.checkoutV2.sectionsState.paymentSection);

  const trackGtmPaymentInfo = useCallback(
    (code: string, checkoutFlowType: CheckoutGTMFlowType | null, paymentInfoPrefilled: boolean) => {
      if (!cartItems || !cartPrices || !code || !checkoutFlowType) {
        return;
      }

      const checkoutAddPaymentInfoData: TrackingEventsAdditionalData[TrackingEvents.TrackingCheckoutAddPaymentInfo] = {
        ...sharedData,
        ...buildCommerceBaseData({ items: cartItems, prices: cartPrices }),
        payment_type: code,
        checkout_flow_type: checkoutFlowType,
        payment_info_prefilled: paymentInfoPrefilled,
        payment_info_edited: paymentSection === "editing",
      };

      dispatchTrackingEvent(TrackingEvents.TrackingCheckoutAddPaymentInfo, checkoutAddPaymentInfoData);
    },
    [cartItems, cartPrices, sharedData, paymentSection]
  );

  return { trackGtmPaymentInfo };
};
